/**
 * Sticky Add-to-Cart Bar
 *
 * Shows when user scrolls past the configured trigger section,
 * or when the main product submit button is out of view (default).
 *
 * Hides when ANY of:
 *   - the footer group is in viewport
 *   - the product-footnotes section (legal text) is in viewport
 *     — avoids overlapping the regulatory copy
 *   - any modal overlay is open (evidence / ingredient detail / etc.)
 *     — defensive: even though z-index puts modals on top, the modal
 *     backdrop is semi-transparent so the bar would ghost through
 *   - the cart drawer (or any other theme modal built on the same
 *     convention — product media modal, pickup availability, etc.) is
 *     open, signaled theme-wide by `body.overflow-hidden`
 *
 * Also toggles `body.sticky-atc-visible` for any CSS that wants to
 * react (e.g. extra bottom padding on a section).
 *
 * RAF-throttled scroll handler. MutationObservers watch modal overlays
 * and `body`'s class list so the bar hides immediately when a modal or
 * the cart drawer opens (not just on scroll — opening the drawer via
 * the sticky bar's own submit button doesn't scroll the page at all).
 */
(function() {
  'use strict';

  // Selectors used for "is a modal open right now" check. Keep in sync
  // with the modal overlay markup across the theme.
  const MODAL_OVERLAY_SELECTOR =
    '.evidence-modal-overlay[aria-hidden="false"], ' +
    '.ingredient-modal-overlay[aria-hidden="false"]';

  let ticking = false;
  let stickyAtc = null;
  let submitBtn = null;
  let footerGroup = null;
  let footnotesSection = null;
  let triggerSection = null;

  function isInViewport(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.bottom >= 0 && rect.top <= window.innerHeight;
  }

  function hasScrolledPast(el) {
    if (!el) return false;
    return el.getBoundingClientRect().top <= window.innerHeight;
  }

  function isAnyModalOpen() {
    if (document.querySelector(MODAL_OVERLAY_SELECTOR)) return true;
    // Cart drawer, product media modal, pickup-availability, etc. all
    // signal "open" by adding this class to body rather than flipping
    // aria-hidden on a dedicated overlay element.
    return document.body.classList.contains('overflow-hidden');
  }

  function updateStickyVisibility() {
    if (!stickyAtc) {
      ticking = false;
      return;
    }

    let shouldShow;

    if (isAnyModalOpen()) {
      // Hard-hide while any modal is open — overrides every other rule.
      shouldShow = false;
    } else {
      const footerVisible = isInViewport(footerGroup);
      const footnotesVisible = isInViewport(footnotesSection);

      if (triggerSection) {
        shouldShow = hasScrolledPast(triggerSection) && !footerVisible && !footnotesVisible;
      } else {
        shouldShow = !isInViewport(submitBtn) && !footerVisible && !footnotesVisible;
      }
    }

    stickyAtc.classList.toggle('shown', shouldShow);
    document.body.classList.toggle('sticky-atc-visible', shouldShow);
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateStickyVisibility);
      ticking = true;
    }
  }

  function init() {
    stickyAtc = document.querySelector('.sticky-atc');
    if (!stickyAtc) return;

    submitBtn = document.querySelector('.product [type="submit"]');
    footerGroup = document.querySelector('.shopify-section-group-footer-group');
    footnotesSection = document.querySelector('.product-footnotes-section');

    var inner = stickyAtc.querySelector('[data-trigger-selector]');
    if (inner) {
      var sel = inner.getAttribute('data-trigger-selector');
      if (sel) triggerSection = document.querySelector(sel);
    }

    updateStickyVisibility();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Watch for modal opens/closes (aria-hidden flips). MutationObserver
    // is the cleanest way: triggers the moment the modal toggles, with
    // no scroll required.
    const overlays = document.querySelectorAll(
      '.evidence-modal-overlay, .ingredient-modal-overlay'
    );
    if (overlays.length && typeof MutationObserver === 'function') {
      const observer = new MutationObserver(onScroll);
      overlays.forEach((overlay) => {
        observer.observe(overlay, { attributes: true, attributeFilter: ['aria-hidden'] });
      });
    }

    // Watch body for the cart drawer / other modals toggling
    // `overflow-hidden` — opening the drawer from the sticky bar's own
    // submit button doesn't fire a scroll event, so the bar would
    // otherwise stay visible on top of the drawer until the next scroll.
    if (typeof MutationObserver === 'function') {
      const bodyObserver = new MutationObserver(onScroll);
      bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
